import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {BroadcastsService} from "../services/broadcasts.service";
import {ActivatedRoute} from "@angular/router";
import {Observable, Subscription} from "rxjs";
import {Broadcast} from "../model/broadcast/broadcast";
import {Game, InningPart} from "../model/broadcast/game";
import {Control} from "../model/broadcast/control";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {map, shareReplay, take} from "rxjs/operators";
import {UtilService} from "../services/util.service";
import {ConfirmDialogComponent} from "./confirm-dialog/confirm-dialog.component";
import {ResetDialogComponent} from "./reset-dialog/reset-dialog.component";
import {UndoDialogComponent} from "./undo-dialog/undo-dialog.component";
import {GamesService} from "../services/games.service";
import {MatDialog} from "@angular/material/dialog";
import {MatGridList, MatGridTile} from "@angular/material/grid-list";
import {MatButton} from "@angular/material/button";
import {StatusComponent} from "../broadcast-layer-module/broadcast-layer/status/status.component";
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {MatIcon} from "@angular/material/icon";

@Component({
  selector: 'app-broadcast-control',
  templateUrl: './broadcast-control.component.html',
  imports: [
    MatGridTile,
    MatIcon,
    MatGridList,
    MatButton,
    StatusComponent,
    AsyncPipe,
    NgForOf,
    NgIf
  ],
  styleUrls: ['./broadcast-control.component.scss']
})
export class BroadcastControlComponent implements OnInit, OnDestroy {

  subscriptions: Subscription[];
  broadcast: Broadcast;
  broadcast$: Observable<Broadcast>;
  game$: Observable<Game>;
  controls: Control[];
  range: number[];

  isDesktop$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Web)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  isTablet$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.TabletLandscape)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  // Status je odbavený do výstupu, když akce jeho controlu není 'off' ('on' / 'animate').
  get statusOnAir(): boolean {
    return this.broadcastService.getControlAction(this.controls, 'status') !== 'off';
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    public broadcastService: BroadcastsService,
    private route: ActivatedRoute,
    private utilService: UtilService,
    private dialog: MatDialog,
    public gamesService: GamesService
  ) {
    this.subscriptions = [];
    this.range = [];
  }

  ngOnInit(): void {
    const uid = this.route.snapshot.paramMap.get('uid');
    this.broadcast$ = this.broadcastService.getBroadcast(uid);
    const controlsSubscription = this.broadcastService.getControls(uid).subscribe(controls => {
      this.controls = controls;
    });
    this.subscriptions.push(controlsSubscription);
    const broadcastSubscription = this.broadcast$.subscribe(broadcast => {
      // `docData` emits undefined for a uid with no document behind it — a
      // deleted broadcast, or a control link somebody kept. Everything below
      // reads `broadcast.config` or writes to `broadcast.uid`, so there is
      // nothing useful to do with that emission but wait for a real one.
      if (!broadcast) {
        return;
      }

      if (!this.broadcast) {
        this.broadcastService.resettingManualData(broadcast, false);
      }

      // Živý stream game pro PBP náhled – docData emituje novou instanci při každém snapshotu,
      // takže se náhled aktualizuje přes ngOnChanges. Navázat jen jednou (broadcast$ emituje opakovaně).
      if (!this.game$ && broadcast?.gameId) {
        this.game$ = this.gamesService.getGame(broadcast.gameId);
      }
      if (!this.range.length
          || this.broadcast?.config?.unites != broadcast.config.unites
          || this.broadcast?.config?.lowerRange != broadcast.config.lowerRange) {
        const isMph = broadcast.config.unites === 'MPH';
        const lowerRange = broadcast.config.lowerRange;
        const lowestSpeed = isMph ? (lowerRange ? 50 : 60) : (lowerRange ? 80 : 100);
        const options = isMph ? 40 : 60;
        this.range = [];
        for (let i = 0; i < options; i++) {
          this.range.push(lowestSpeed + i);
        }
      }

      this.gamesService.getGame(broadcast?.gameId).pipe(take(1)).subscribe(game => {
        this.broadcast = broadcast;
        if (!broadcast.manualData) {
          this.broadcast.manualData = this.utilService.parseToManualData(game);
          this.broadcastService.updateManualData(this.broadcast);
        }
      });
    });
    this.subscriptions.push(broadcastSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (this.dialog.openDialogs.length) {
      return;
    }
    if (event.code === "KeyA") {
      this.ball();
    } else if(event.code === "KeyS") {
      this.strike();
    } else if(event.code === "KeyF") {
      this.foul();
    } else if(event.code === "KeyP") {
      this.inPlay();
    } else if(event.code === "KeyY") {
      this.sync();
    } else if(event.code === "KeyR") {
      this.reset();
    } else if(event.code === "ArrowLeft") {
      this.toggle('third');
    } else if(event.code === "ArrowUp") {
      this.toggle('second');
    } else if(event.code === "ArrowRight") {
      this.toggle('first');
    } else if(event.code === "KeyO") {
      this.out();
    } else if(event.code === "KeyU") {
      this.undo();
    } else if(event.code === "KeyI") {
      this.updateInning(event.shiftKey);
    } else if(event.code === "KeyG") {
      this.awayRun(event.shiftKey ? -1 : null);
    } else if(event.code === "KeyH") {
      this.homeRun(event.shiftKey ? -1 : null);
    } else if(event.code === "KeyT") {
      this.updateInningPart('T');
    } else if(event.code === "KeyM") {
      this.updateInningPart('M');
    } else if(event.code === "KeyB") {
      this.updateInningPart('B');
    } else if(event.code === "KeyE") {
      this.updateInningPart('E');
    } else if(event.code === "Backspace") {
      this.broadcast.radarSpeed = null;
    } else if(event.code.startsWith('Digit') || event.code.startsWith('Numpad')) {
      if (!this.broadcast.radarSpeed) {
        this.broadcast.radarSpeed = Number(event.key);
      } else {
        this.broadcast.radarSpeed = this.broadcast.radarSpeed * 10 + Number(event.key)
      }
    } else {
      console.log(event);
    }
  }

  ball(): Promise<void> {
    this.playball();
    this.broadcast.manualData.balls++;
    this.incrementPitches();
    if (this.broadcast.manualData.balls > 3) {
      this.resetCount();
      this.reachFirst();
    }
    this.updateSpeedIfSet();
    return this.broadcastService.updateManualData(this.broadcast);
  }

  private incrementPitches(): void {
    if (this.broadcast.manualData.inningPart === 'T' && this.broadcast.manualData.homePitches != null) {
      this.broadcast.manualData.homePitches++;
    }
    if (this.broadcast.manualData.inningPart === 'B' && this.broadcast.manualData.awayPitches != null) {
      this.broadcast.manualData.awayPitches++;
    }
  }

  private resetCount(): void {
    this.broadcast.manualData.balls = 0;
    this.broadcast.manualData.strikes = 0;
  }

  strike(): Promise<void> {
    this.playball();
    this.broadcast.manualData.strikes++;
    this.incrementPitches();
    this.updateSpeedIfSet();
    if (this.broadcast.manualData.strikes > 2) {
      this.resetCount();
      return this.out();
    } else {
      return this.broadcastService.updateManualData(this.broadcast);
    }
  }

  foul(): Promise<void> {
    this.playball();
    if (this.broadcast.manualData.strikes < 2) {
      this.broadcast.manualData.strikes++;
    }
    this.incrementPitches();
    this.updateSpeedIfSet();

    return this.broadcastService.updateManualData(this.broadcast);
  }

  out(): Promise<void> {
    this.broadcast.manualData.outs++;
    if (this.broadcast.manualData.outs > 2) {
      if (this.broadcast.manualData.inningPart === 'T') {
        this.broadcast.manualData.inningPart = 'M';
      }
      if (this.broadcast.manualData.inningPart === 'B') {
        this.broadcast.manualData.inningPart = 'E';
      }
      this.broadcast.manualData.outs = 0;
    }
    return this.broadcastService.updateManualData(this.broadcast);
  }

  homeRun(value?: number): Promise<void> {
    if (value != null && value > -1) {
      this.broadcast.manualData.home = value;
    }
    if (value === -1) {
      if (this.broadcast.manualData.home > 0) {
        this.broadcast.manualData.home--;
      }
    } else {
      this.broadcast.manualData.home++;
    }
    return this.broadcastService.updateManualData(this.broadcast);
  }

  awayRun(value?: number): Promise<void> {
    if (value != null && value > -1) {
      this.broadcast.manualData.away = value;
    }
    if (value === -1) {
      if (this.broadcast.manualData.away > 0) {
        this.broadcast.manualData.away--;
      }
    } else {
      this.broadcast.manualData.away++;
    }
    return this.broadcastService.updateManualData(this.broadcast);
  }

  inPlay(): Promise<void> {
    this.playball();
    this.incrementPitches();
    this.resetCount();
    this.updateSpeedIfSet();
    return this.broadcastService.updateManualData(this.broadcast);
  }

  updateInningPart(inningPart: InningPart) {
    this.broadcast.manualData.inningPart = inningPart;
    return this.broadcastService.updateManualData(this.broadcast);
  }

  reachFirst() {
    if (this.broadcast.manualData.first && !this.broadcast.manualData.second) {
      this.broadcast.manualData.second = true;
    } else if (this.broadcast.manualData.first && this.broadcast.manualData.second && !this.broadcast.manualData.third) {
      this.broadcast.manualData.third = true;
    } else if (this.broadcast.manualData.first && this.broadcast.manualData.second && this.broadcast.manualData.third) {
      if (this.broadcast.manualData.inningPart === 'T') {
        this.broadcast.manualData.away++;
      }
      if (this.broadcast.manualData.inningPart === 'B') {
        this.broadcast.manualData.home++;
      }
    } else {
      this.broadcast.manualData.first = true;
    }
  }

  updateInning(dec?: boolean) {
    if (!dec) {
      this.broadcast.manualData.inning++;
    } else {
      if (this.broadcast.manualData.inning > 1) {
        this.broadcast.manualData.inning--;
      }
    }
    return this.broadcastService.updateManualData(this.broadcast);
  }

  toggle(param: string) {
    this.broadcast.manualData[param] = !this.broadcast.manualData[param];
    return this.broadcastService.updateManualData(this.broadcast);
  }

  setSpeed(speed: number) {
    if (this.broadcast.radarSpeed === speed) {
      this.broadcast.radarSpeed = null;
    } else {
      this.broadcast.radarSpeed = speed;
    }
  }

  updateSpeedIfSet(): Promise<void> {
    if (this.broadcast.radarSpeed) {
      return this.broadcastService.updateRadarSpeed(this.broadcast);
    }
    this.setSpeed(null);
  }

  private playball() {
    if (this.broadcast.manualData.inningPart === 'E') {
      this.broadcast.manualData.inning++;
      this.broadcast.manualData.inningPart = 'T';
      this.emptyBases();
      this.resetCount();
      this.broadcast.manualData.outs = 0;
    }
    if (this.broadcast.manualData.inningPart === 'M') {
      this.broadcast.manualData.inningPart = 'B';
      this.emptyBases();
      this.resetCount();
      this.broadcast.manualData.outs = 0;
    }
  }

  private emptyBases() {
    this.broadcast.manualData.first = false;
    this.broadcast.manualData.second = false;
    this.broadcast.manualData.third = false;
  }

  sync(): void {
    if(this.broadcast?.gameId) {
      this.gamesService.getGame(this.broadcast.gameId).pipe(take(1)).subscribe(game => {

        if (this.broadcast.manualData?.inningPart === game?.inningPart && this.broadcast.manualData?.inning === game?.inning) {
          this.broadcast.manualData = this.utilService.parseToManualData(game);
          return this.broadcastService.updateManualData(this.broadcast);
        } else {
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '390px',
            data: this.broadcast,
          });
          dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
            if (result === 'confirmed' && game) {
              this.broadcast.manualData = this.utilService.parseToManualData(game);
              return this.broadcastService.updateManualData(this.broadcast);
            }
          });
        }
      });
    }
  }

  reset() {
    this.broadcastService.resettingManualData(this.broadcast, true).finally(() => {
      this.dialog.open(ResetDialogComponent, {
        width: '390px',
        data: this.broadcast,
      }).afterClosed().pipe(take(1)).subscribe(next => {
        this.broadcastService.resettingManualData(this.broadcast, false);
      });
    });
  }

  undo() {
    this.broadcastService.resettingManualData(this.broadcast, true).finally(() => {
      this.dialog.open(UndoDialogComponent, {
        width: '390px',
        data: this.broadcast.uid,
      }).afterClosed().pipe(take(1)).subscribe(next => {
        this.broadcastService.resettingManualData(this.broadcast, false);
      });
    });
  }
}
